<template>
	<tr>
		<td class="c_table_w40 bookmark" @click="onClickBookmark">
			<IconThinStar v-if="!props.data.bookmark" width="24" height="24" /><IconThinStarOn
				v-if="props.data.bookmark"
				width="24"
				height="24"
			/>
		</td>
		<td v-if="isNo" class="c_table_w40">{{ props.idx + 1 }}</td>
		<td class="c_table_auto title">
			<div class="title_box">
				<img :src="props.data.file_path" alt="종목 이미지" loading="lazy" />
				<div class="title_box_content">
					<span class="company">
						{{ props.data.major_holder_proportion_name ?? props.data.event_name }}
					</span>
					<span class="stock">
						{{ props.data.major_holder_proportion_ticker ?? props.data.event_ticker }}
					</span>
				</div>
			</div>
		</td>
		<td
			class="c_table_w80 table_item"
			:class="[
				getColorClass(
					props.data.event_list_proportion_change ??
						props.data.major_holder_proportion_change ??
						props.data.event_list_proportion
				),
			]"
		>
			<span
				v-if="
					props.data.event_list_proportion_change != null ||
					props.data.major_holder_proportion_change != null ||
					props.data.event_list_proportion != null
				"
				>{{
					`${formatWithSign(props.data.event_list_proportion_change ?? props.data.major_holder_proportion_change ?? props.data.event_list_proportion)}%`
				}}</span
			>
		</td>
		<td v-if="isPercent" class="c_table_w50 table_item">
			<span v-if="props.data.event_list_proportion != null || props.data.major_holder_proportion != null">{{
				`${props.data.event_list_proportion ?? props.data.major_holder_proportion}%`
			}}</span>
		</td>
		<td><div class="search_btn" @click="onClickMoveToReport">분석</div></td>
	</tr>
</template>

<script setup lang="ts">
// core
import { useRouter } from 'vue-router';

// api
import { useAdjustBookmarksApi } from '@/api/Interest';
import { EventList } from '@/api/majorHolder';
// constant
import {
	CANCEL,
	COMPANY_TITLE,
	CONFIRM_LOGIN,
	LOGIN_REQUIRED_PAGE,
	MESSAGE_CANCEL_COMPLETED,
	UNFAVORITE_PROMPT,
} from '@/constants/title';
// store
import { useLoginPopupStore } from '@/stores/LoginPopup';
import { usePopupStore } from '@/stores/popup';
import { useToastStore } from '@/stores/toast';
import { useTokenStore } from '@/stores/token';
// util
import { formatWithSign } from '@/utils/function/format';

// component
import IconThinStar from '../icon/IconThinStar.vue';
import IconThinStarOn from '../icon/IconThinStarOn.vue';

// -----variable-----
const props = defineProps<{
	data: EventList;
	idx: number;
	isNo?: boolean;
	isPercent?: boolean;
}>();
const emit = defineEmits(['update:book-mark-refetch']);

const popupStore = usePopupStore();
const toastStore = useToastStore();
const tokenStore = useTokenStore();
const loginPopupStore = useLoginPopupStore();
const router = useRouter();

// -----api-----
const { mutateAsync: adjustBookmarks } = useAdjustBookmarksApi();

// -----function-----
// 개별 북마크 클릭 시 실행 함수
const onClickBookmark = async () => {
	if (!tokenStore.isLogin) {
		loginPopupStore.openPopup({
			icon: 'alert',
			content: LOGIN_REQUIRED_PAGE,
			confirm: () => router.push('/auth/login'),
			confirmText: CONFIRM_LOGIN,
			cancelText: CANCEL,
		});
		return;
	}
	const params = [
		{
			event_pk: Number(props.data.event_pk),
			master_pk: null,
		},
	];
	if (props.data.bookmark) {
		popupStore.openPopup({
			content: COMPANY_TITLE,
			subtext: UNFAVORITE_PROMPT,
			confirm: async () => {
				const res = await adjustBookmarks({
					bookmarks: params,
				});
				if (res.data.success) {
					toastStore.openToast({
						subContent: COMPANY_TITLE,
						content: MESSAGE_CANCEL_COMPLETED,
					});
					emit('update:book-mark-refetch');
				}
			},
		});
	} else {
		const res = await adjustBookmarks({
			bookmarks: params,
		});
		if (res.data.success) {
			emit('update:book-mark-refetch');
		}
	}
};

const getColorClass = (score: string) => {
	if (!score) {
		return;
	}

	if (String(score).includes('-')) {
		return 'blue';
	} else if (String(score) == '0') {
		return 'white';
	} else {
		return 'red';
	}
};

const onClickMoveToReport = async () => {
	router.push(`/report/usa/${props.data.event_pk}`);
};
</script>

<style lang="scss" scoped>
table,
th,
td {
	border: 1px solid var(--color-base-mid-dark);
	border-collapse: collapse;
}
tbody {
	td {
		font-size: var(--font-s-small);
		font-weight: var(--font-w-semi-bold);
	}
}
.bookmark {
	// display: table-cell;
	text-align: center; /* 수평 중앙 정렬 */
	vertical-align: middle;
	cursor: pointer;
	svg {
		display: inline-block; /* SVG 또는 아이콘을 인라인 블록으로 설정 */
		vertical-align: middle; /* SVG 또는 아이콘을 수직 중앙 정렬 */
	}
}
.title {
	text-align: center; /* 수평 중앙 정렬 */
	vertical-align: middle;
	padding: 0.9rem 1rem;
	&_box {
		display: flex;
		align-items: center;
		gap: 0.6rem;
		img {
			width: 3rem;
			height: 3rem;
			border-radius: 999px;
		}
		&_content {
			display: flex;
			flex-direction: column;
			align-items: start;
			font-size: var(--font-s-small);
			line-height: normal;
			white-space: nowrap;
			.company {
				font-weight: var(--font-w-semi-bold);
			}
			.stock {
				color: var(--color-base-light);
				font-weight: var(--font-w-light);
			}
		}
	}
}
.red {
	color: var(--color-danger);
}
.blue {
	color: var(--color-secondary-Blue);
}
.table_item {
	width: 8rem;
	padding: 0.8rem 1rem;
}
.search_btn {
	cursor: pointer;
	display: flex;
	width: 5.8rem;
	height: 3rem;
	justify-content: center;
	align-items: center;
	border-radius: 100rem;
	background: var(--color-primary);
	font-size: var(--font-s-small);
	font-weight: var(--font-w-semi-bold);
	line-height: 1.8rem;
	margin: 0.8rem 1rem;
}
</style>
